import React, { createContext, useContext, useState, ReactNode } from 'react';
import GlobalDialog, { DialogButton } from '../components/common/GlobalDialogContainer';

interface DialogConfig {
  title: string;
  content: ReactNode;
  buttons?: DialogButton[];
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  showCloseButton?: boolean;
  dividers?: boolean;
  onClose?: () => void;
}

interface DialogContextType {
  openDialog: (config: DialogConfig) => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState<DialogConfig | null>(null);

  const openDialog = (config: DialogConfig) => {
    setDialogConfig(config);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    if (dialogConfig?.onClose) {
      dialogConfig.onClose();
    }
    // Clear config after animation
    setTimeout(() => setDialogConfig(null), 300);
  };

  const handleClose = () => {
    closeDialog();
  };

  return (
    <DialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      {dialogConfig && (
        <GlobalDialog
          open={isOpen}
          onClose={handleClose}
          title={dialogConfig.title}
          buttons={dialogConfig.buttons}
          maxWidth={dialogConfig.maxWidth}
          fullWidth={dialogConfig.fullWidth}
          showCloseButton={dialogConfig.showCloseButton}
          dividers={dialogConfig.dividers}
        >
          {dialogConfig.content}
        </GlobalDialog>
      )}
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};
